import { LegacyRef, FC, useMemo } from 'react';
import { Combobox, Flex, Select, SelectItem } from '@highereducation/hen-ui';
import { QuestionProps } from '@de/components/Step/Step';
import { useDynamicExperienceContext } from '@de/providers/DynamicExperienceProvider';
import { Option } from '@de/types/types';
import useQuestionEvents from '@de/hooks/useQuestionEvents';
import { OptionsLoadingPlaceholder } from '@de/components/Step/OptionsLoadingPlaceholder';
import { ErrorMessage } from '@de/components/ErrorMessage';
import {
  optionToComboboxOption,
  partitionSearchBarOptions,
} from '@de/utils/searchBarOptions';

const DropdownQuestion: FC<QuestionProps> = ({
  options = [],
  id = '',
  leadFieldAssociated,
  placeholderText = 'Select an option',
  automaticNext = false,
  isMultiQuestionChild = false,
  title,
  stepName,
  variant,
  isOptionsLoading = false,
  searchBarAllOptions,
  dynamicOptionsError,
}) => {
  const { updateFlowState, getStepMetadataById, handleStepForward } =
    useDynamicExperienceContext();
  const currentStepData = getStepMetadataById(id);
  const selectedOption = currentStepData.option as Option;

  const { ref: visibilityRef, triggerFieldInputted } = useQuestionEvents({
    id,
    fieldType: 'dropdown',
    options,
    title,
    stepName,
    isMultiQuestionChild,
    variant,
    leadFieldAssociated,
  });

  const hasSearchBar = Boolean(searchBarAllOptions?.length);

  const { displayedOptions, searchableOptions } = useMemo(() => {
    if (!hasSearchBar) {
      return { displayedOptions: options, searchableOptions: [] as Option[] };
    }
    return partitionSearchBarOptions(
      options,
      searchBarAllOptions as Option[],
      selectedOption && !Array.isArray(selectedOption) ? [selectedOption] : [],
    );
  }, [hasSearchBar, options, searchBarAllOptions, selectedOption]);

  const selectByValue = (value: string) => {
    const pool = searchBarAllOptions ?? options;
    const selectedOpt = pool.find((opt) => opt.optionId.toString() === value);

    if (selectedOpt) {
      triggerFieldInputted(selectedOpt.text as string);

      updateFlowState(id, { option: selectedOpt, leadFieldAssociated });

      if (automaticNext && !isMultiQuestionChild) {
        handleStepForward();
      }
    }
  };

  const showLoading = isOptionsLoading && !dynamicOptionsError;

  return (
    <Flex direction='column' ref={visibilityRef as LegacyRef<HTMLDivElement>}>
      {dynamicOptionsError ? (
        <ErrorMessage
          message={dynamicOptionsError}
          className='w-full justify-center py-2 px-2'
        />
      ) : null}
      {showLoading ? (
        <OptionsLoadingPlaceholder count={4} />
      ) : (
        <>
          <Select
            id={id}
            value={selectedOption?.optionId?.toString() || ''}
            placeholder={placeholderText}
            onValueChange={selectByValue}
          >
            {displayedOptions.map((option) => (
              <SelectItem
                key={option.optionId}
                value={option.optionId.toString()}
              >
                {option.text}
              </SelectItem>
            ))}
          </Select>
          {hasSearchBar && searchableOptions.length > 0 && (
            <Combobox
              className='mt-4'
              aria-label={title || 'Search options'}
              options={searchableOptions.map(optionToComboboxOption)}
              selectedValues={
                selectedOption?.optionId ? [selectedOption.optionId] : []
              }
              onSelect={selectByValue}
            />
          )}
        </>
      )}
    </Flex>
  );
};

export default DropdownQuestion;
